import { compressToEncodedURIComponent, decompressFromEncodedURIComponent } from 'lz-string';


/**
 * @param {Array} [columnHeaders] - Array of column header objects with value and label for restoring filter labels
 * @returns {{ page: number, rowsPerPage: number, searchQuery: string, filters: Array, logic: string, sortConfig: Object }}
 */
export const getUrlParams = (columnHeaders = null) => {
  const params = new URLSearchParams(window.location.search);
  const pageParam = parseInt(params.get('page'), 10);
  const page = pageParam > 0 ? pageParam - 1 : 0; // Convert 1-based to 0-based
  const rowsPerPage = parseInt(params.get('rowsPerPage'), 10) || 20;
  
  // Search
  const searchQuery = params.get('search') || '';
  
  // Sort
  const sortKey = params.get('sortKey') || 'username';
  const sortDirection = params.get('sortDirection') || 'asc';
  const sortConfig = { key: sortKey, direction: sortDirection };
  
  // Filters
  const compressedFilters = params.get('filters');
  const logic = params.get('logic') || 'and';
  
  let filters = [];
  if (compressedFilters) {
    try {
      const filtersString = decompressFromEncodedURIComponent(compressedFilters);
      
      if (filtersString) {
        filters = filtersString
          .split('|')
          .map(filterStr => {
            const parts = filterStr.split(':').map(decodeURIComponent);
            const [column, operator, value, columnLabel, label] = parts;
            if (!column || !operator) return null;
            if (!value && operator !== 'is empty' && operator !== 'is not empty') return null;
            
            // Restore labels from columnHeaders if not present in URL
            const columnHeader = columnHeaders?.find(col => col.key === column);
            return { 
              id: `${Date.now()}-${Math.random().toString(36).substring(2, 11)}`,
              column, 
              operator, 
              value: value || '',
              columnLabel: columnLabel || columnHeader?.label || '',
              label: label || value || ''
            };
          })
          .filter(Boolean);
      }
    } catch (error) {
      console.error('Error parsing filters from URL:', error);
    }
  }

  return { page, rowsPerPage, searchQuery, filters, logic, sortConfig };
};

/**
 * @param {Object} params - Object containing all parameters to update
 * @param {number} [params.page] - Current page number (0-based)
 * @param {number} [params.rowsPerPage] - Number of rows per page
 * @param {string} [params.searchQuery] - Search query string
 * @param {Array} [params.filters] - Array of filters
 * @param {string} [params.logic] - 'and' or 'or' for filters
 * @param {Object} [params.sortConfig] - Sort configuration { key, direction }
 */
export const updateUrlParams = (params) => {
  const currentUrl = new URL(window.location.href);
  const urlParams = new URLSearchParams(currentUrl.search);

  if (params.page !== undefined) {
    const urlPage = params.page + 1;
    if (urlPage > 1) {
      urlParams.set('page', urlPage.toString());
    } else {
      urlParams.delete('page');
    }
  }
  
  if (params.rowsPerPage !== undefined) {
    if (params.rowsPerPage !== 20) {
      urlParams.set('rowsPerPage', params.rowsPerPage.toString());
    } else {
      urlParams.delete('rowsPerPage');
    }
  }

  // Update search
  if (params.searchQuery !== undefined) {
    if (params.searchQuery && params.searchQuery.trim()) {
      urlParams.set('search', params.searchQuery.trim());
    } else {
      urlParams.delete('search');
    }
  }

  // Update sort
  if (params.sortConfig !== undefined) {
    const { key, direction } = params.sortConfig;
    if (key !== 'username' || direction !== 'asc') {
      urlParams.set('sortKey', key);
      urlParams.set('sortDirection', direction);
    } else {
      urlParams.delete('sortKey');
      urlParams.delete('sortDirection');
    }
  }

  // Update filters
  if (params.filters !== undefined) {
    const validLogic = params.logic === 'or' ? 'or' : 'and';
    
    if (params.filters.length > 0) {
      const filtersString = params.filters
        .map(({ column, operator, value, columnLabel, label }) => 
          `${encodeURIComponent(column)}:${encodeURIComponent(operator)}:${encodeURIComponent(value || '')}:${encodeURIComponent(columnLabel || '')}:${encodeURIComponent(label || '')}`
        )
        .join('|');
      
      const compressedFilters = compressToEncodedURIComponent(filtersString);
      urlParams.set('filters', compressedFilters);
      urlParams.set('logic', validLogic); 
    } else {
      urlParams.delete('filters');
      urlParams.delete('logic');
    }
  }

  currentUrl.search = urlParams.toString();
  window.history.replaceState({}, '', currentUrl);
};

